<template>
  <div class="rec-page">
    <BackButton />
    <div class="rec-head">
      <h2>Receivables</h2>
      <p class="rec-sub">Open invoices, who owes you what, and how long it's been outstanding.</p>
    </div>

    <div v-if="loading" class="rec-loading"><i class="mdi mdi-loading mdi-spin"></i> Loading…</div>
    <template v-else>
      <div class="rec-summary">
        <div class="rec-total-card">
          <div class="rec-total-lbl">Total Outstanding</div>
          <div class="rec-total-val">KES {{ Number(totalOutstanding).toLocaleString() }}</div>
        </div>
        <div class="rec-aging">
          <div v-for="(amt, k) in agingDisplay" :key="k" class="rec-aging-cell" :class="'bucket-'+k">
            <div class="rec-aging-lbl">{{ agingLabel(k) }}</div>
            <div class="rec-aging-val">KES {{ Number(amt).toLocaleString() }}</div>
          </div>
        </div>
      </div>

      <div v-if="!invoices.length" class="rec-empty">
        <i class="mdi mdi-check-circle-outline"></i>
        <p>No open receivables. You're all paid up.</p>
      </div>
      <div v-else class="table-responsive"><table class="rec-table">
        <thead>
          <tr>
            <th>Invoice</th><th>Client</th><th>Issued</th><th>Due</th>
            <th class="num">Total</th><th class="num">Paid</th><th class="num">Balance</th><th>Status</th><th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="inv in invoices" :key="inv.id" :class="{overdue: inv.status==='overdue'}">
            <td><strong>{{ inv.invoice_number }}</strong></td>
            <td>{{ inv.company_name }}</td>
            <td>{{ inv.issue_date }}</td>
            <td>{{ inv.due_date }} <span v-if="inv.days_overdue > 0" class="rec-od">({{ inv.days_overdue }}d)</span></td>
            <td class="num">{{ formatKes(inv.total_kes) }}</td>
            <td class="num">{{ formatKes(inv.paid_kes) }}</td>
            <td class="num bal" :class="{warn: Number(inv.balance_kes) > 0}">{{ formatKes(inv.balance_kes) }}</td>
            <td><span class="rec-status" :class="'st-'+inv.status">{{ statusLabel(inv.status) }}</span></td>
            <td><router-link :to="'/receivables/invoice/'+inv.id" class="rec-link">View</router-link></td>
          </tr>
        </tbody>
      </table></div>
    </template>
  </div>
</template>

<script>
import { executeJson, flocker } from "@/api";
import BackButton from "@/components/BackButton.vue";
export default {
  name: "ReceivablesPage",
  components: { BackButton },
  data(){return{
    loading: true,
    invoices: [],
    totalOutstanding: 0,
    aging: { current:0, "1_30":0, "31_60":0, "61_90":0, over_90:0 },
  }},
  computed:{
    agingDisplay(){
      return { current:this.aging.current, "1_30":this.aging["1_30"], "31_60":this.aging["31_60"], "61_90":this.aging["61_90"], over_90:this.aging.over_90 };
    }
  },
  mounted(){ this.load(); },
  methods:{
    async load(){
      this.loading = true;
      try {
        const r = await executeJson({ function: "getReceivables", sellerId: sessionStorage.getItem("userId") }, flocker);
        if (r.data && r.data.success) {
          this.invoices = r.data.data.invoices || [];
          this.aging = r.data.data.aging || this.aging;
          this.totalOutstanding = r.data.data.totalOutstanding || 0;
        }
      } catch (e){ /* silent */ }
      this.loading = false;
    },
    formatKes(v){ return Number(v || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}); },
    agingLabel(k){
      return ({ current: "Not yet due", "1_30":"1–30 d overdue", "31_60":"31–60 d", "61_90":"61–90 d", over_90:"90+ d" })[k] || k;
    },
    statusLabel(s){
      return ({ sent:"Sent", viewed:"Viewed", partially_paid:"Partial", paid:"Paid", overdue:"Overdue", draft:"Draft", cancelled:"Cancelled" })[s] || s;
    }
  }
};
</script>

<style scoped>
.rec-page{max-width:1200px;margin:0 auto;padding:24px 20px 80px;color:#1A1A1A}
html[data-theme="dark"] .rec-page{color:#F5F1E8}
.rec-head h2{margin:0 0 4px;font-size:24px;letter-spacing:-0.3px}
.rec-sub{margin:0 0 18px;color:#6B6356;font-size:13px}
html[data-theme="dark"] .rec-sub{color:#A8A095}
.rec-loading{text-align:center;padding:60px 20px;color:#8A827A}
.rec-summary{display:grid;grid-template-columns:280px 1fr;gap:14px;margin-bottom:24px}
.rec-total-card{background:linear-gradient(135deg,#8B3F1E,#6E3217);color:#FFF;padding:18px 20px;border-radius:12px}
.rec-total-lbl{font-size:11px;letter-spacing:1px;text-transform:uppercase;opacity:.8}
.rec-total-val{font-size:28px;font-weight:700;margin-top:4px}
.rec-aging{display:grid;grid-template-columns:repeat(5,1fr);gap:8px}
.rec-aging-cell{background:#FFFFFF;border:1px solid #E5E0D5;padding:12px 14px;border-radius:10px;text-align:center}
html[data-theme="dark"] .rec-aging-cell{background:#1C1A16;border-color:#3A352B}
.rec-aging-cell.bucket-current{border-left:4px solid #1F6B1F}
.rec-aging-cell.bucket-1_30{border-left:4px solid #E0B040}
.rec-aging-cell.bucket-31_60{border-left:4px solid #D88838}
.rec-aging-cell.bucket-61_90{border-left:4px solid #C45A2C}
.rec-aging-cell.bucket-over_90{border-left:4px solid #B73E2F}
.rec-aging-lbl{font-size:10px;color:#8A827A;text-transform:uppercase;letter-spacing:.5px}
.rec-aging-val{font-size:14px;font-weight:600;margin-top:2px}
.rec-empty{text-align:center;padding:60px;color:#1F6B1F}
.rec-empty i{font-size:48px}
.rec-table{width:100%;border-collapse:collapse;font-size:13px;background:#FFFFFF;border-radius:10px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,.05)}
html[data-theme="dark"] .rec-table{background:#1C1A16}
.rec-table th{background:#F5F1E8;color:#6B6356;text-align:left;font-weight:600;padding:10px 14px;font-size:11px;text-transform:uppercase;letter-spacing:.5px}
html[data-theme="dark"] .rec-table th{background:#252119;color:#A8A095}
.rec-table th.num,.rec-table td.num{text-align:right}
.rec-table td{padding:10px 14px;border-top:1px solid #F0EBE4}
html[data-theme="dark"] .rec-table td{border-color:#2A2620}
.rec-table tr.overdue{background:#FEF6F4}
html[data-theme="dark"] .rec-table tr.overdue{background:#2F1F1A}
.rec-od{color:#B73E2F;font-size:11px;font-weight:600}
.rec-table td.bal.warn{color:#B73E2F;font-weight:600}
.rec-status{font-size:10px;font-weight:700;padding:2px 8px;border-radius:10px;text-transform:uppercase;letter-spacing:.5px}
.rec-status.st-sent{background:#E8E0D0;color:#5C4533}
.rec-status.st-viewed{background:#D6E5F0;color:#1F4F7A}
.rec-status.st-partially_paid{background:#FFE0B2;color:#8B5A2B}
.rec-status.st-paid{background:#C8E6C9;color:#1F6B1F}
.rec-status.st-overdue{background:#FFCDD2;color:#B71C1C}
.rec-status.st-draft{background:#E0E0E0;color:#666}
.rec-link{color:#BFA164;text-decoration:none;font-weight:600}
.rec-link:hover{color:#A8854F}
@media (max-width:768px){
  .rec-summary{grid-template-columns:1fr}
  .rec-aging{grid-template-columns:repeat(2,1fr)}
  .rec-table{font-size:11px}
  .rec-table th,.rec-table td{padding:6px 8px}
}
</style>
